import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
// Imported from the source module rather than the `utils` barrel: this renders
// inside five pages, several of which mock the whole barrel in their tests.
import { colors } from "utils/general";
import { useStandardsContext } from "hooks/useStandards/useStandards";

/**
 * What the standards pages render while `standardsData` is empty.
 *
 * Empty means one of two things — still fetching, or the fetch failed — and the
 * pages can't tell them apart from the array alone, so a failed request used to
 * spin forever. This reads the shared standards query directly (same queryKey,
 * `enabled: false`, so it never issues its own request) and shows a recoverable
 * error instead. `refetch` ignores `staleTime`, so Retry works without a reload.
 */
export const StandardsFallback = ({ size }) => {
  const { isError, refetch } = useStandardsContext(true);

  if (!isError) return <FontAwesomeIcon icon={faSpinner} spin size={size} />;

  return (
    // Deliberately not role="alert": three of the five pages gate their sidebar
    // and main panel on the same expression, so both render at once and two
    // assertive live regions would announce over each other. The text is
    // visible and the button is reachable in normal reading order.
    <Container>
      <Message>Couldn't load standards.</Message>
      <RetryButton type="button" onClick={() => refetch()}>
        Retry
      </RetryButton>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
`;

const Message = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: ${colors.milkGlass950};
`;

const RetryButton = styled.button`
  border: 1px solid ${colors.blue500};
  border-radius: 6px;
  background: transparent;
  color: ${colors.blue500};
  font-size: 0.875rem;
  font-weight: 600;
  padding: 6px 16px;

  &:hover {
    background: ${colors.blue50};
  }
`;
